import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  PenLine, Package, Users, Award, Globe, Share2,
  Plus, Trash2, Save, CheckCircle2, AlertCircle,
  Eye, Wand2, ExternalLink, Loader2, Building2,
  Linkedin, Instagram, Twitter, Youtube, PartyPopper, ChevronRight, CreditCard
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import ImageUpload from '../../components/ImageUpload';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'overview',       label: 'Overview',        icon: PenLine },
  { id: 'products',       label: 'Products',         icon: Package },
  { id: 'team',           label: 'Team',             icon: Users },
  { id: 'certifications', label: 'Certifications',   icon: Award },
  { id: 'coverage',       label: 'Coverage',         icon: Globe },
  { id: 'social',         label: 'Social',           icon: Share2 },
  { id: 'payment',        label: 'Ödeme Bilgileri',  icon: CreditCard },
];

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white';
const labelCls = 'block text-xs font-semibold text-gray-600 mb-1';

const ONBOARDING_STEPS = [
  { tab: 'overview',       label: 'Company Overview',   desc: 'Logo, tagline, about' },
  { tab: 'products',       label: 'Products',           desc: 'What you supply' },
  { tab: 'coverage',       label: 'Coverage',           desc: 'Countries you serve' },
  { tab: 'certifications', label: 'Certifications',     desc: 'Accreditations & quality marks' },
  { tab: 'social',         label: 'Social Links',       desc: 'LinkedIn, website' },
];

// ── Confetti Animation ────────────────────────────────────────────────────────
const CONFETTI_COLORS_S = ['#a855f7', '#f97316', '#16a34a', '#3b82f6', '#ec4899', '#eab308'];
const ConfettiBlastS = ({ onDone }) => {
  const particles = useRef(
    Array.from({ length: 50 }, (_, i) => ({
      id: i,
      color: CONFETTI_COLORS_S[i % CONFETTI_COLORS_S.length],
      left: `${(i / 50) * 100 + (Math.random() - 0.5) * 8}%`,
      delay: `${(i / 50) * 0.6}s`,
      duration: `${1.0 + (i % 5) * 0.15}s`,
      size: `${6 + (i % 4) * 2}px`,
      shape: i % 3 === 0 ? '50%' : i % 3 === 1 ? '0' : '2px',
    }))
  ).current;
  useEffect(() => { const t = setTimeout(onDone, 2500); return () => clearTimeout(t); }, [onDone]);
  return (
    <div className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden">
      {particles.map(p => (
        <div key={p.id} style={{
          position: 'absolute', top: '-20px', left: p.left,
          width: p.size, height: p.size, backgroundColor: p.color,
          borderRadius: p.shape, animationDelay: p.delay, animationDuration: p.duration,
          animation: `confettiFall ${p.duration} ${p.delay} ease-in forwards`,
        }} />
      ))}
      <style>{`@keyframes confettiFall { 0% { transform: translateY(0) rotate(0deg) scale(1); opacity: 1; } 100% { transform: translateY(105vh) rotate(720deg) scale(0.8); opacity: 0; } }`}</style>
    </div>
  );
};

const emptyTeamMember   = () => ({ id: `stm_${Date.now()}`, name: '', role: '', bio: '', avatar_url: '', linkedin: '' });
const emptyCertification = () => ({ id: `sct_${Date.now()}`, name: '', issuer: '', year: new Date().getFullYear() });

const COUNTRY_PRESETS_S = [
  { name: 'Turkey', flag: '🇹🇷', cities: ['Istanbul', 'Ankara', 'Izmir'] },
  { name: 'Germany', flag: '🇩🇪', cities: ['Berlin', 'Düsseldorf', 'Munich', 'Frankfurt'] },
  { name: 'France', flag: '🇫🇷', cities: ['Paris', 'Lyon', 'Cannes'] },
  { name: 'Italy', flag: '🇮🇹', cities: ['Milan', 'Bologna', 'Rome'] },
  { name: 'Spain', flag: '🇪🇸', cities: ['Barcelona', 'Madrid'] },
  { name: 'UAE', flag: '🇦🇪', cities: ['Dubai', 'Abu Dhabi'] },
  { name: 'United Kingdom', flag: '🇬🇧', cities: ['London', 'Birmingham'] },
  { name: 'Netherlands', flag: '🇳🇱', cities: ['Amsterdam', 'Utrecht'] },
  { name: 'Switzerland', flag: '🇨🇭', cities: ['Basel', 'Geneva'] },
  { name: 'Saudi Arabia', flag: '🇸🇦', cities: ['Riyadh', 'Jeddah'] },
];

export default function SupplierProfile() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => location.state?.tab || 'overview');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [orgSlug, setOrgSlug] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const prevScoreRef = useRef(null);
  const [profileScore, setProfileScore] = useState(null);

  const [autoSaveStatus, setAutoSaveStatus] = useState(''); // '' | 'saving' | 'saved'
  const autoSaveTimerRef = useRef(null);

  // State — must be declared BEFORE useCallback/useEffect that reference them
  const [overview, setOverview] = useState({
    about: '', tagline: '', cover_image_url: '', logo_url: '',
    stats: { products_listed: 0, countries_shipped: 0, orders_completed: 0, years_in_business: 0 }
  });
  const [categories, setCategories] = useState(['General']);
  const [newCategory, setNewCategory] = useState('');
  const [team, setTeam]                   = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [coverage, setCoverage]             = useState({ countries: [], shipping_note: '' });
  const [newCountry, setNewCountry]         = useState({ flag: '', name: '', cities: '' });
  const [social, setSocial]                 = useState({ website: '', linkedin: '', instagram: '', twitter: '', youtube: '' });
  const [paymentInfo, setPaymentInfo]       = useState({ bank_name: '', account_holder: '', iban: '', swift_bic: '', payment_notes: '' });

  // Debounced auto-save — triggered whenever overview/social/catalog changes
  const scheduleAutoSave = React.useCallback(() => {
    setAutoSaveStatus('saving');
    clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      if (!token) return;
      try {
        await axios.patch(`${API}/api/supplier/profile`, {
          tagline: overview.tagline,
          about: overview.about,
          logo_url: overview.logo_url,
          cover_image_url: overview.cover_image_url,
          social,
        }, { headers: { Authorization: `Bearer ${token}` } });
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus(''), 2000);
      } catch {
        setAutoSaveStatus('');
      }
    }, 1500);
  // eslint-disable-next-line
  }, [overview, social]);

  useEffect(() => {
    if (!loading) scheduleAutoSave();
  // eslint-disable-next-line
  }, [overview.tagline, overview.about, overview.logo_url, overview.cover_image_url]);

  // ── Profile Completion Score (mirrors backend logic) ─────────────────────────
  const computeScore = (ov = overview, cats = categories, cert = certifications, cov = coverage, soc = social) => {
    const flags = [
      Boolean(ov.about?.trim()),
      Boolean(ov.tagline?.trim()),
      Boolean(ov.logo_url?.trim()),
      Boolean(ov.cover_image_url?.trim()),
      cats.length > 0 && !(cats.length === 1 && cats[0] === 'General'),
      cats.length > 0,
      Boolean(soc.website?.trim()),
      cert.length > 0,
    ];
    return Math.round(flags.filter(Boolean).length / flags.length * 100);
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
        const { data } = await axios.get(`${API}/api/supplier/profile`, { headers: { Authorization: `Bearer ${token}` } });
        const p = data.profile || {};
        const o = data.org || {};
        setOrgSlug(o.display_name ? o.display_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') : '');
        const ov = {
          about: p.about || '',
          tagline: p.tagline || '',
          cover_image_url: p.cover_image_url || '',
          logo_url: p.logo_url || '',
          stats: p.stats || { products_listed: 0, countries_shipped: 0, orders_completed: 0, years_in_business: 0 }
        };
        const cats = p.catalog?.categories || ['General'];
        const cert = p.certifications || [];
        const cov = p.coverage || { countries: [], shipping_note: '' };
        const soc = p.social || { website: '', linkedin: '', instagram: '', twitter: '', youtube: '' };
        setOverview(ov);
        setCategories(cats);
        setTeam(p.team || []);
        setCertifications(cert);
        setCoverage(cov);
        setSocial(soc);
        setPaymentInfo(p.payment_info || { bank_name: '', account_holder: '', iban: '', swift_bic: '', payment_notes: '' });
        // Initialize prevScoreRef for confetti baseline
        prevScoreRef.current = computeScore(ov, cats, cert, cov, soc);
        setProfileScore(prevScoreRef.current);
      } catch (e) {
        console.error(e);
        showToast('error', 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const handleSave = async () => {
    if (!overview.tagline?.trim() && !overview.about?.trim()) {
      showToast('error', t('validation.required') + ' — Tagline veya About alanını doldurun');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      const payload = {
        about: overview.about,
        tagline: overview.tagline,
        cover_image_url: overview.cover_image_url,
        logo_url: overview.logo_url,
        stats: overview.stats,
        catalog: { categories, items: [] },
        team,
        certifications,
        coverage,
        social,
        payment_info: (paymentInfo.bank_name || paymentInfo.iban) ? { ...paymentInfo } : null,
      };
      await axios.patch(`${API}/api/supplier/profile`, payload, { headers: { Authorization: `Bearer ${token}` } });
      // Profile completeness check for confetti
      const score = computeScore();
      if (score === 100 && prevScoreRef.current !== null && prevScoreRef.current < 100) {
        setShowConfetti(true);
      }
      prevScoreRef.current = score;
      setProfileScore(score);
      showToast('success', score === 100 ? 'Profile 100% Complete! 🎉' : 'Profile saved successfully!');
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleFillTestData = () => {
    setOverview({
      tagline: 'Premium Corporate & Exhibition Staff Uniforms Since 2012',
      about: 'BrandWear Uniforms Ltd is a leading manufacturer and supplier of custom corporate uniforms, branded workwear, and event staff apparel. Headquartered in Istanbul with distribution hubs in London and Dubai, we have been dressing exhibition teams, trade show staff, and corporate personnel with precision and quality for over a decade.\n\nOur in-house design studio works closely with clients to develop bespoke uniform collections that reflect brand identity while ensuring comfort and durability. From polo shirts and blazers to custom aprons and branded accessories, we provide full wardrobe solutions tailored to every event environment.\n\nWe operate ISO 9001:2015 certified manufacturing facilities and are proud to offer OEKO-TEX Standard 100 certified fabrics across our premium range. With fast turnaround times and competitive MOQ requirements, we serve exhibition organizers, stand contractors, and corporate events teams in 18+ countries.',
      cover_image_url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?q=80&w=2200&auto=format&fit=crop',
      logo_url: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?q=80&w=200&auto=format&fit=crop',
      stats: { products_listed: 280, countries_shipped: 18, orders_completed: 4200, years_in_business: 12 }
    });
    setCategories(['Exhibition Staff Uniforms', 'Corporate Blazers & Jackets', 'Custom Polo & T-Shirts', 'Branded Aprons & Vests', 'Promotional Accessories']);
    setTeam([
      { id: 'stm_demo_1', name: 'Selin Yılmaz', role: 'CEO & Founder', bio: 'With 18 years in the textile and corporate apparel industry, Selin leads BrandWear\'s global strategy and client partnerships. She founded the company in 2012 after a decade managing uniform programs for Fortune 500 exhibition teams.', avatar_url: 'https://images.unsplash.com/photo-1494790108755-2616b612b7c4?q=80&w=200&auto=format&fit=crop', linkedin: 'https://linkedin.com/in/selin-yilmaz-brandwear' },
      { id: 'stm_demo_2', name: 'Mehmet Çelik', role: 'Head of Design', bio: 'Mehmet oversees all creative direction and uniform design, bringing a background in fashion design and textile engineering. He ensures every uniform reflects the client\'s brand identity with precision and style.', avatar_url: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=200&auto=format&fit=crop', linkedin: 'https://linkedin.com/in/mehmet-celik-design' },
      { id: 'stm_demo_3', name: 'Sarah Thompson', role: 'Sales Manager – Europe & Middle East', bio: 'Sarah manages all European and Middle Eastern client accounts, coordinating bulk orders for major trade shows including Hannover Messe, GITEX, and Salone del Mobile. Fluent in English, German, and French.', avatar_url: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=200&auto=format&fit=crop', linkedin: 'https://linkedin.com/in/sarah-thompson-sales' },
    ]);
    setCertifications([
      { id: 'sct_demo_1', name: 'OEKO-TEX Standard 100', issuer: 'OEKO-TEX Association', year: 2023 },
      { id: 'sct_demo_2', name: 'ISO 9001:2015', issuer: 'TÜV Rheinland', year: 2022 },
      { id: 'sct_demo_3', name: 'BSCI Compliance', issuer: 'Business Social Compliance Initiative', year: 2023 },
      { id: 'sct_demo_4', name: 'GOTS Certified', issuer: 'Global Organic Textile Standard', year: 2022 },
    ]);
    setCoverage({
      countries: [
        { flag: '🇹🇷', name: 'Türkiye', cities: ['Istanbul', 'Ankara', 'Izmir'], primary: true },
        { flag: '🇬🇧', name: 'United Kingdom', cities: ['London', 'Birmingham'], primary: false },
        { flag: '🇩🇪', name: 'Germany', cities: ['Frankfurt', 'Dusseldorf', 'Munich'], primary: false },
        { flag: '🇦🇪', name: 'UAE', cities: ['Dubai', 'Abu Dhabi'], primary: false },
        { flag: '🇸🇦', name: 'Saudi Arabia', cities: ['Riyadh', 'Jeddah'], primary: false },
        { flag: '🇳🇱', name: 'Netherlands', cities: ['Amsterdam', 'Rotterdam'], primary: false },
      ],
      shipping_note: 'Express delivery available across Europe (2-3 business days). International orders to Middle East and Asia in 5-8 business days. Bulk orders (500+ units) include free express shipping.'
    });
    setSocial({
      website: 'https://brandwearuniforms.com',
      linkedin: 'https://linkedin.com/company/brandwear-uniforms',
      instagram: 'https://instagram.com/brandwear_uniforms',
      twitter: 'https://twitter.com/brandwear',
      youtube: '',
    });
    showToast('success', 'All tabs filled with demo data. Review and click Save All Changes.');
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="animate-spin h-8 w-8 text-purple-400" />
    </div>
  );

  /* ── OVERVIEW TAB ──────────────────────────────────────────────────── */
  const overviewTab = (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
        <h3 className="text-sm font-bold text-gray-700">Company Introduction</h3>
        <div>
          <label className={labelCls}>Tagline / Slogan</label>
          <input className={inputCls} value={overview.tagline} onChange={e => setOverview(p => ({ ...p, tagline: e.target.value }))} placeholder="e.g. Premium Corporate & Event Staff Uniforms" />
        </div>
        <div>
          <label className={labelCls}>About / Company Description</label>
          <textarea className={`${inputCls} resize-none`} rows={6} value={overview.about} onChange={e => setOverview(p => ({ ...p, about: e.target.value }))} placeholder="Describe your company, expertise, and what makes you unique..." />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ImageUpload
            value={overview.cover_image_url}
            onChange={v => setOverview(p => ({ ...p, cover_image_url: v }))}
            token={localStorage.getItem('access_token') || sessionStorage.getItem('access_token')}
            label="Kapak Görseli"
            hint="Önerilen: 1200×400px"
            aspectRatio="cover"
            data-testid="supplier-cover-upload"
          />
          <ImageUpload
            value={overview.logo_url}
            onChange={v => setOverview(p => ({ ...p, logo_url: v }))}
            token={localStorage.getItem('access_token') || sessionStorage.getItem('access_token')}
            label="Logo"
            hint="PNG, JPG, WEBP"
            aspectRatio="square"
            data-testid="supplier-logo-upload"
          />
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
        <h3 className="text-sm font-bold text-gray-700">Key Statistics</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { key: 'products_listed', label: 'Products Listed' },
            { key: 'countries_shipped', label: 'Countries Shipped' },
            { key: 'orders_completed', label: 'Orders Completed' },
            { key: 'years_in_business', label: 'Years in Business' },
          ].map(({ key, label }) => (
            <div key={key}>
              <label className={labelCls}>{label}</label>
              <input type="number" className={inputCls} value={overview.stats[key] || 0}
                onChange={e => setOverview(p => ({ ...p, stats: { ...p.stats, [key]: parseInt(e.target.value) || 0 } }))} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  /* ── PRODUCTS TAB (categories) ─────────────────────────────────────── */
  const productsTab = (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
      <div>
        <h3 className="text-sm font-bold text-gray-700 mb-1">Product Categories</h3>
        <p className="text-xs text-gray-500">Add the product/service categories you supply. These appear on your public profile.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {categories.map(cat => (
          <span key={cat} className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-full text-sm text-purple-700 font-medium">
            {cat}
            <button onClick={() => { if (categories.length > 1) setCategories(prev => prev.filter(c => c !== cat)); }}
              className="text-purple-400 hover:text-purple-700 transition-colors">
              <Trash2 className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input className={inputCls} placeholder="Add category (e.g. Exhibition Staff Uniforms)" value={newCategory}
          onChange={e => setNewCategory(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && newCategory.trim() && !categories.includes(newCategory.trim())) { setCategories(p => [...p, newCategory.trim()]); setNewCategory(''); } }} />
        <button onClick={() => { if (newCategory.trim() && !categories.includes(newCategory.trim())) { setCategories(p => [...p, newCategory.trim()]); setNewCategory(''); } }}
          disabled={!newCategory.trim()}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-colors whitespace-nowrap">
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
    </div>
  );

  /* ── TEAM TAB ─────────────────────────────────────────────────────── */
  const teamTab = (
    <div className="space-y-4">
      {team.map((m, idx) => (
        <div key={m.id} className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-gray-700">Team Member #{idx + 1}</p>
            <button onClick={() => setTeam(p => p.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Name</label><input className={inputCls} value={m.name} onChange={e => setTeam(p => p.map((t, i) => i === idx ? { ...t, name: e.target.value } : t))} placeholder="Full Name" /></div>
            <div><label className={labelCls}>Role / Title</label><input className={inputCls} value={m.role} onChange={e => setTeam(p => p.map((t, i) => i === idx ? { ...t, role: e.target.value } : t))} placeholder="e.g. CEO & Founder" /></div>
            <div><label className={labelCls}>Avatar URL</label><input className={inputCls} value={m.avatar_url} onChange={e => setTeam(p => p.map((t, i) => i === idx ? { ...t, avatar_url: e.target.value } : t))} placeholder="https://..." /></div>
            <div><label className={labelCls}>LinkedIn URL</label><input className={inputCls} value={m.linkedin} onChange={e => setTeam(p => p.map((t, i) => i === idx ? { ...t, linkedin: e.target.value } : t))} placeholder="https://linkedin.com/in/..." /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Bio</label><textarea className={`${inputCls} resize-none`} rows={3} value={m.bio} onChange={e => setTeam(p => p.map((t, i) => i === idx ? { ...t, bio: e.target.value } : t))} placeholder="Short biography..." /></div>
          </div>
          {m.avatar_url && <img src={m.avatar_url} alt={m.name} className="h-16 w-16 rounded-full object-cover border-2 border-purple-200" />}
        </div>
      ))}
      <button onClick={() => setTeam(p => [...p, emptyTeamMember()])}
        className="w-full py-3 border-2 border-dashed border-purple-200 hover:border-purple-400 text-purple-600 hover:text-purple-700 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
        <Plus className="h-4 w-4" /> Add Team Member
      </button>
    </div>
  );

  /* ── CERTIFICATIONS TAB ───────────────────────────────────────────── */
  const certificationsTab = (
    <div className="space-y-4">
      {certifications.map((c, idx) => (
        <div key={c.id} className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-bold text-gray-700">Certification #{idx + 1}</p>
            <button onClick={() => setCertifications(p => p.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div><label className={labelCls}>Certificate Name</label><input className={inputCls} value={c.name} onChange={e => setCertifications(p => p.map((ct, i) => i === idx ? { ...ct, name: e.target.value } : ct))} placeholder="e.g. ISO 9001:2015" /></div>
            <div><label className={labelCls}>Issuing Organization</label><input className={inputCls} value={c.issuer} onChange={e => setCertifications(p => p.map((ct, i) => i === idx ? { ...ct, issuer: e.target.value } : ct))} placeholder="e.g. TÜV Rheinland" /></div>
            <div><label className={labelCls}>Year Obtained</label><input type="number" className={inputCls} value={c.year} onChange={e => setCertifications(p => p.map((ct, i) => i === idx ? { ...ct, year: parseInt(e.target.value) || 2020 } : ct))} placeholder="2023" /></div>
          </div>
        </div>
      ))}
      <button onClick={() => setCertifications(p => [...p, emptyCertification()])}
        className="w-full py-3 border-2 border-dashed border-purple-200 hover:border-purple-400 text-purple-600 hover:text-purple-700 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
        <Plus className="h-4 w-4" /> Add Certification
      </button>
    </div>
  );

  /* ── COVERAGE TAB ─────────────────────────────────────────────────── */
  const coverageTab = (
    <div className="space-y-4">
      {/* Quick-add presets */}
      <div className="bg-purple-50 rounded-xl p-3 border border-purple-100">
        <p className="text-[11px] font-semibold text-purple-700 mb-2">Quick Add — Popular Countries</p>
        <div className="flex flex-wrap gap-1.5">
          {COUNTRY_PRESETS_S.map(preset => {
            const alreadyAdded = (coverage.countries || []).some(c => c.name === preset.name);
            return (
              <button key={preset.name} disabled={alreadyAdded}
                onClick={() => !alreadyAdded && setCoverage(p => ({ ...p, countries: [...(p.countries || []), { flag: preset.flag, name: preset.name, cities: preset.cities, primary: false }] }))}
                className={`text-xs px-2 py-1 rounded-full border transition-all ${alreadyAdded ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-gray-700 border-gray-200 hover:border-purple-400 hover:text-purple-700 cursor-pointer'}`}>
                {preset.flag} {preset.name}
              </button>
            );
          })}
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-3">
        <h3 className="text-sm font-bold text-gray-700">Shipping / Delivery Note</h3>
        <textarea className={`${inputCls} resize-none`} rows={3} value={coverage.shipping_note} onChange={e => setCoverage(p => ({ ...p, shipping_note: e.target.value }))} placeholder="Describe your delivery capabilities, lead times, and shipping regions..." />
      </div>
      {(coverage.countries || []).map((c, idx) => (
        <div key={idx} className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-bold text-gray-700">Country #{idx + 1}</p>
            <button onClick={() => setCoverage(p => ({ ...p, countries: p.countries.filter((_, i) => i !== idx) }))} className="text-red-400 hover:text-red-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className={labelCls}>Flag Emoji</label><input className={inputCls} value={c.flag} onChange={e => setCoverage(p => ({ ...p, countries: p.countries.map((ct, i) => i === idx ? { ...ct, flag: e.target.value } : ct) }))} placeholder="🇹🇷" /></div>
            <div><label className={labelCls}>Country Name</label><input className={inputCls} value={c.name} onChange={e => setCoverage(p => ({ ...p, countries: p.countries.map((ct, i) => i === idx ? { ...ct, name: e.target.value } : ct) }))} placeholder="Türkiye" /></div>
            <div className="flex items-center gap-2 pt-5">
              <input type="checkbox" id={`primary_${idx}`} checked={c.primary || false} onChange={e => setCoverage(p => ({ ...p, countries: p.countries.map((ct, i) => i === idx ? { ...ct, primary: e.target.checked } : ct) }))} className="accent-purple-600" />
              <label htmlFor={`primary_${idx}`} className="text-xs font-semibold text-gray-600">HQ / Primary</label>
            </div>
            <div className="col-span-3"><label className={labelCls}>Cities (comma-separated)</label><input className={inputCls} value={Array.isArray(c.cities) ? c.cities.join(', ') : c.cities} onChange={e => setCoverage(p => ({ ...p, countries: p.countries.map((ct, i) => i === idx ? { ...ct, cities: e.target.value.split(',').map(s => s.trim()).filter(Boolean) } : ct) }))} placeholder="Istanbul, Ankara, Izmir" /></div>
          </div>
        </div>
      ))}
      <button onClick={() => setCoverage(p => ({ ...p, countries: [...(p.countries || []), { flag: '', name: '', cities: [], primary: false }] }))}
        className="w-full py-3 border-2 border-dashed border-purple-200 hover:border-purple-400 text-purple-600 hover:text-purple-700 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
        <Plus className="h-4 w-4" /> Add Country
      </button>
    </div>
  );

  /* ── SOCIAL TAB ───────────────────────────────────────────────────── */
  const socialTab = (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
      <h3 className="text-sm font-bold text-gray-700 mb-2">Social Media & Web Presence</h3>
      {[
        { key: 'website', label: 'Website', icon: Globe, placeholder: 'https://brandwearuniforms.com' },
        { key: 'linkedin', label: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/company/...' },
        { key: 'instagram', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/...' },
        { key: 'twitter', label: 'Twitter / X', icon: Twitter, placeholder: 'https://twitter.com/...' },
        { key: 'youtube', label: 'YouTube', icon: Youtube, placeholder: 'https://youtube.com/@...' },
      ].map(({ key, label, icon: Icon, placeholder }) => (
        <div key={key} className="flex items-center gap-3">
          <div className="w-9 h-9 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon className="h-4 w-4 text-purple-600" />
          </div>
          <div className="flex-1">
            <label className={labelCls}>{label}</label>
            <input className={inputCls} value={social[key] || ''} onChange={e => setSocial(p => ({ ...p, [key]: e.target.value }))} placeholder={placeholder} />
          </div>
          {social[key] && (
            <a href={social[key]} target="_blank" rel="noopener noreferrer" className="mt-4 text-purple-600 hover:text-purple-800">
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
      ))}
    </div>
  );

  const renderTab = () => {
    switch (activeTab) {
      case 'overview':        return overviewTab;
      case 'products':        return productsTab;
      case 'team':            return teamTab;
      case 'certifications':  return certificationsTab;
      case 'coverage':        return coverageTab;
      case 'social':          return socialTab;
      case 'payment':         return paymentTab;
      default: return null;
    }
  };

  const paymentTab = (
    <Card className="border-0 shadow-sm max-w-2xl">
      <CardHeader className="pb-4">
        <CardTitle className="text-sm flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-purple-600" />Fatura Ödeme Bilgileri
        </CardTitle>
        <p className="text-xs text-gray-400 mt-1">Bu bilgiler müşterilerinize göndereceğiniz faturalarda gösterilir.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Banka Adı</label>
            <input className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-purple-300 outline-none"
              placeholder="Ziraat Bankası"
              value={paymentInfo.bank_name}
              onChange={e => setPaymentInfo(p => ({ ...p, bank_name: e.target.value }))}
              data-testid="sup-payment-bank-name" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Hesap Sahibi</label>
            <input className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-purple-300 outline-none"
              placeholder="Tedarikçi A.Ş."
              value={paymentInfo.account_holder}
              onChange={e => setPaymentInfo(p => ({ ...p, account_holder: e.target.value }))}
              data-testid="sup-payment-account-holder" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">IBAN</label>
          <input className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-purple-300 outline-none"
            placeholder="TR12 0001 0017 4531 0823 1045 39"
            value={paymentInfo.iban}
            onChange={e => setPaymentInfo(p => ({ ...p, iban: e.target.value.toUpperCase() }))}
            data-testid="sup-payment-iban" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">SWIFT / BIC</label>
          <input className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-purple-300 outline-none"
            placeholder="TCZBTR2A"
            value={paymentInfo.swift_bic}
            onChange={e => setPaymentInfo(p => ({ ...p, swift_bic: e.target.value.toUpperCase() }))}
            data-testid="sup-payment-swift" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Ödeme Notu</label>
          <textarea className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-purple-300 outline-none resize-none" rows={2}
            placeholder="Ödeme açıklamasına fatura numarasını yazınız."
            value={paymentInfo.payment_notes}
            onChange={e => setPaymentInfo(p => ({ ...p, payment_notes: e.target.value }))}
            data-testid="sup-payment-notes" />
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6 pb-10">
      {showConfetti && <ConfettiBlastS onDone={() => setShowConfetti(false)} />}
      {showConfetti && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 shadow-sm animate-pulse">
          <PartyPopper className="h-6 w-6 text-purple-600" />
          <div>
            <p className="font-bold text-purple-800">Profile 100% Complete!</p>
            <p className="text-xs text-purple-600 mt-0.5">Your profile now has maximum visibility. Exhibitors and contractors can find you more easily!</p>
          </div>
        </div>
      )}
      {/* Profile Score Bar */}
      {profileScore !== null && !showConfetti && (
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-gray-600">Profile Completion</span>
              <span className={`text-xs font-bold ${profileScore === 100 ? 'text-emerald-600' : profileScore >= 60 ? 'text-amber-600' : 'text-red-500'}`}>{profileScore}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div className={`h-1.5 rounded-full transition-all duration-500 ${profileScore === 100 ? 'bg-emerald-500' : profileScore >= 60 ? 'bg-amber-500' : 'bg-red-400'}`} style={{ width: `${profileScore}%` }} />
            </div>
          </div>
          {profileScore < 100 && (
            <span className="text-xs text-gray-400 whitespace-nowrap">{8 - Math.round(profileScore / 12.5)} fields left</span>
          )}
          {profileScore === 100 && <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />}
        </div>
      )}
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Company Profile</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your public profile visible to exhibitors and contractors</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="gap-2 border-dashed border-purple-300 text-purple-700 hover:bg-purple-50 hover:border-purple-400"
            onClick={handleFillTestData} data-testid="fill-demo-btn">
            <Wand2 className="h-4 w-4" /> Fill Demo Data
          </Button>
          {orgSlug && (
            <Button variant="outline" size="sm" className="gap-2 text-gray-600"
              onClick={() => navigate(`/supplier/${orgSlug}`)} data-testid="view-public-btn">
              <Eye className="h-4 w-4" /> View Public Page
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
            data-testid="save-profile-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
          {autoSaveStatus === 'saved' && (
            <span className="text-xs text-green-600 font-medium flex items-center gap-1" data-testid="autosave-indicator">
              <CheckCircle2 className="h-3.5 w-3.5" /> Otomatik kaydedildi
            </span>
          )}
          {autoSaveStatus === 'saving' && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Kaydediliyor...
            </span>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`flex items-center gap-3 p-4 rounded-xl text-sm font-medium shadow ${toast.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {toast.type === 'success' ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <AlertCircle className="h-5 w-5 text-red-500" />}
          {toast.msg}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              data-testid={`supplier-profile-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${activeTab === t.id ? 'bg-white text-purple-700 shadow-sm border border-purple-100' : 'text-gray-500 hover:text-gray-700'}`}>
              <Icon className="h-4 w-4" />{t.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {renderTab()}
    </div>
  );
}
